// Adherence Detection Logic
// Pattern matching and analysis for detecting adherence gaps, adverse events, and profile changes
// from call transcripts in real-time

// ============================================
// TYPES
// ============================================
export interface AdherenceFlag {
  id: string;
  callId: string;
  timestamp: string;
  patientName: string;
  patientPhone: string;
  type: "adherence_gap" | "adverse_event" | "profile_change" | "clinical_concern";
  severity: "low" | "medium" | "high";
  details: {
    medicationsDeclined?: string[];
    medicationsAccepted?: string[];
    eventDescription?: string;
    transcriptExcerpt?: string;
    detectedPatterns?: string[];
  };
  clinicalPillar: "necessity" | "effectiveness" | "safety" | "adherence" | "unmet_needs";
  interventionRecommendation: string;
  acknowledged: boolean;
  // Follow-up tracking fields
  followUpStatus?: "pending" | "in_progress" | "completed" | "not_required";
  followUpCallId?: string;
  followUpTimestamp?: string;
  followUpSentiment?: {
    overall: "positive" | "neutral" | "concerned" | "stressed";
    notes: string;
    keyMoments?: string[];
  };
}

export interface TranscriptMessage {
  role: "user" | "assistant" | "system" | "bot";
  message: string;
  time?: string | number;
}

export interface DetectionResult {
  hasFlags: boolean;
  flags: Omit<AdherenceFlag, "id" | "callId" | "timestamp" | "patientName" | "patientPhone" | "acknowledged">[];
}

// ============================================
// DETECTION PATTERNS
// ============================================

// Patterns indicating patient is declining medications
const DECLINE_PATTERNS = [
  // Direct declines
  /don'?t want (?:the |that |this )?(?:one|medication|medicine|drug|prescription|refill)?/i,
  /skip (?:the |that |this )?(?:one|medication|medicine|second|third|refill)?/i,
  /not (?:today|this time|right now|that one|the second|the third|needed)/i,
  /just (?:the )?(?:first|one|single) (?:one|medication|medicine|refill)?/i,
  /only (?:want|need|refill) (?:the )?(?:one|first|single)/i,
  /no(?:,| )?(?:I )?(?:don'?t )?(?:need|want) (?:the |that |this )?(?:other|second|third)?/i,
  /(?:cancel|stop|discontinue) (?:the |that |my )?(?:medication|medicine|prescription|refill)/i,
  /not taking (?:the |that |this )?(?:one|medication|anymore|it)/i,
  /ran out and (?:didn'?t|don'?t) (?:need|want)/i,
  /(?:I'?ll |let me )?(?:pass|skip) on (?:the |that )?(?:one|second|third)?/i,
  /don'?t need (?:it|that|the|this)/i,
  /no,? (?:thanks|thank you|not that)/i,
  /I'?m (?:good|fine|okay) (?:without|on) (?:that|it|the)/i,
  /(?:hold off|wait) on (?:that|the|this)/i,
  /not (?:refilling|getting|picking up) (?:that|the|this)/i,
  // Additional decline patterns
  /(?:I |I'?ll )?(?:pass|skip|decline) (?:on )?(?:that|this|the)/i,
  /(?:leave|leave out|exclude) (?:the |that |this )?(?:one|medication)/i,
  /(?:can you |please )?(?:remove|take off|take out) (?:the |that )?(?:one|medication)/i,
  /(?:I )?stopped taking (?:the |that |this |my )?/i,
  /haven'?t been taking (?:the |that |this |my )?/i,
  /(?:quit|stopped|gave up) (?:on )?(?:the |that |my )?(?:medication|medicine|pills?)/i,
  /(?:not |no longer )?(?:using|taking) (?:the |that |this )(?:one|medication)/i,
  /(?:decided|going) to stop (?:taking |using )?(?:the |that |my )?/i,
  /(?:don'?t |won'?t )(?:be )?(?:taking|needing|wanting) (?:the |that |this )?/i,
  /(?:threw|throw) (?:it |them |the medication )?(?:out|away)/i,
  /(?:getting rid|got rid) of (?:the |that |my )?(?:medication|medicine)/i,
  // Partial fills
  /(?:just|only) (?:give me |want |need |fill )?(?:some of|a few|half)/i,
  /(?:partial fill|partial refill|don'?t fill all)/i,
  // Expressions of stopping
  /(?:stopping|quitting|discontinuing) (?:the |that |my )?(?:medication|medicine|treatment)/i,
  /(?:no more|done with|finished with) (?:the |that |my )?(?:medication|medicine)/i,
  // "Don't want to take it anymore" variations
  /(?:don'?t|doesn'?t) want to take (?:it|them|this|that|the medication) (?:anymore|any more|no more)/i,
  /(?:don'?t|doesn'?t) want to (?:be on|use|continue) (?:it|this|that|the medication) (?:anymore|any more)/i,
  /(?:tired of|sick of|fed up with) taking (?:it|this|that|these|the medication)/i,
  /(?:I |I'?m )?(?:done|finished|through) (?:with )?taking (?:it|this|that|these)/i,
  /(?:not going to|won'?t|refuse to) take (?:it|this|that|them) (?:anymore|again)/i,
  /(?:had enough|enough of) (?:this |that |the )?(?:medication|medicine|pills?)/i,
  // Cost-related declines
  /(?:can'?t afford|too expensive) (?:to refill|that one|all of them)/i,
  /(?:skip|skipping) (?:this |that |the )?(?:month|refill|one) (?:because|due to) (?:cost|price|money)/i,
];

// Specific adverse event patterns with descriptions and recommendations
interface AdverseEventPattern {
  pattern: RegExp;
  description: string;
  recommendation: string;
}

const ADVERSE_EVENT_PATTERNS_WITH_CONTEXT: AdverseEventPattern[] = [
  // ============================================
  // INJECTION SITE ISSUES
  // ============================================
  {
    // Common STT errors: "injection site" -> "age I suicide", "edge of site", "inject inside", etc.
    pattern: /(?:pain|sore|soreness|a? ?lot of pain|lots of pain) (?:at |near |around |in )?(?:the |my )?(?:injection site|inject(?:ion)?|age I suicide|edge.?site|shot)/i,
    description: "Pain at injection site",
    recommendation: "Pharmacist counsel on rotating injection sites and proper injection technique",
  },
  {
    pattern: /(?:injection site|where I inject|shot area|inject(?:ion)?|age I suicide) (?:is |feels |looks )?(?:sore|red|swollen|painful|bruised|hurts)/i,
    description: "Injection site reaction",
    recommendation: "Assess injection site reaction severity, counsel on cold compress and site rotation",
  },
  {
    pattern: /(?:bruising|bruise) (?:at |from |after )?(?:the |my )?(?:injection|shot|inject)/i,
    description: "Injection site bruising",
    recommendation: "Review injection technique, counsel on applying pressure post-injection",
  },
  {
    pattern: /(?:lump|bump|hard spot|knot) (?:at |from |after |near )?(?:the |my )?(?:injection|shot|where I inject)/i,
    description: "Injection site lump/nodule",
    recommendation: "Assess for lipodystrophy, counsel on injection site rotation and technique",
  },

  // ============================================
  // CARDIOVASCULAR ISSUES
  // ============================================
  {
    pattern: /(?:chest pain|chest tightness|chest discomfort|pressure in.*chest)/i,
    description: "Chest pain/discomfort",
    recommendation: "URGENT: Assess for cardiac symptoms, may require immediate medical evaluation",
  },
  {
    pattern: /(?:heart|heartbeat) (?:is |feels? )?(?:racing|pounding|fast|irregular|skipping)/i,
    description: "Heart palpitations",
    recommendation: "Assess for arrhythmia, review medications that affect heart rate, consider cardiac evaluation",
  },
  {
    pattern: /(?:palpitation|palpitations|heart flutter)/i,
    description: "Heart palpitations",
    recommendation: "Assess for arrhythmia, review stimulant medications, consider ECG if persistent",
  },
  {
    pattern: /(?:blood pressure|bp) (?:is |has been )?(?:high|low|up|down|elevated|dropped)/i,
    description: "Blood pressure change",
    recommendation: "Verify BP reading, assess medication effects, consider dose adjustment",
  },
  {
    pattern: /(?:feel|feeling) (?:like )?(?:my )?heart (?:is )?(?:going to |gonna )?(?:stop|explode|give out)/i,
    description: "Cardiac anxiety/concern",
    recommendation: "Assess for panic symptoms vs cardiac issues, consider evaluation if persistent",
  },

  // ============================================
  // RESPIRATORY ISSUES
  // ============================================
  {
    pattern: /(?:shortness of breath|short of breath|can'?t breathe|difficulty breathing|hard to breathe|trouble breathing)/i,
    description: "Shortness of breath",
    recommendation: "URGENT: Assess respiratory status, check for medication-related causes, consider medical evaluation",
  },
  {
    pattern: /(?:cough|coughing) (?:that |which )?(?:won'?t |doesn'?t )?(?:go away|stop|persistent)/i,
    description: "Persistent cough",
    recommendation: "Evaluate for ACE inhibitor cough or other medication-related cause, consider alternative therapy",
  },
  {
    pattern: /(?:dry cough|persistent cough|chronic cough)/i,
    description: "Chronic cough",
    recommendation: "Review ACE inhibitor or other medications, assess for alternative causes",
  },
  {
    pattern: /(?:wheezing|wheeze|whistling.*breath)/i,
    description: "Wheezing",
    recommendation: "Assess for bronchospasm, review beta-blocker or NSAID use, consider pulmonary evaluation",
  },

  // ============================================
  // MUSCLE/JOINT ISSUES
  // ============================================
  {
    pattern: /muscle (?:pain|aches|aching|weakness|cramps)/i,
    description: "Muscle pain/weakness",
    recommendation: "Evaluate for potential statin-induced myopathy, consider CK levels and clinical review",
  },
  {
    pattern: /(?:my |feel )?(?:legs?|arms?) (?:feel |are )?(?:weak|heavy|tired|painful)/i,
    description: "Limb weakness or pain",
    recommendation: "Assess for myopathy symptoms, consider dose adjustment or alternative therapy",
  },
  {
    pattern: /joint (?:pain|aches|stiffness|swelling)/i,
    description: "Joint pain or stiffness",
    recommendation: "Evaluate for medication-related arthralgia, consider rheumatology referral if persistent",
  },
  {
    pattern: /(?:back pain|backache|my back (?:hurts|aches|is killing me))/i,
    description: "Back pain",
    recommendation: "Assess for musculoskeletal cause, review medication side effects, consider PT referral",
  },
  {
    pattern: /(?:cramp|cramping|charley horse) (?:in |at )?(?:my |the )?(?:leg|calf|foot|muscle)/i,
    description: "Muscle cramps",
    recommendation: "Assess electrolyte levels, review diuretic or statin use, counsel on hydration",
  },

  // ============================================
  // GENERAL PAIN
  // ============================================
  {
    pattern: /(?:a lot of|lot of|lots of|so much|severe|significant|really bad|terrible) pain/i,
    description: "Significant pain reported",
    recommendation: "Assess pain location, severity, and duration - may require clinical evaluation",
  },
  {
    pattern: /(?:lots and lots|a lot and a lot) (?:of )?pain/i,
    description: "Severe pain reported",
    recommendation: "Urgent pain assessment needed - identify source and severity for intervention",
  },
  {
    pattern: /(?:chronic pain|constant pain|pain all the time|pain every day)/i,
    description: "Chronic pain reported",
    recommendation: "Comprehensive pain assessment needed, consider pain management referral",
  },

  // ============================================
  // GI ISSUES
  // ============================================
  {
    pattern: /(?:stomach|abdominal|belly) (?:pain|cramps|cramping|upset|discomfort)/i,
    description: "GI distress - abdominal discomfort",
    recommendation: "Counsel on taking medication with food, assess for dose adjustment or GI protective therapy",
  },
  {
    pattern: /(?:nausea|nauseous|queasy|sick to my stomach)/i,
    description: "Nausea",
    recommendation: "Recommend taking with food, consider anti-nausea timing or alternative formulation",
  },
  {
    pattern: /(?:throwing up|vomiting|vomit|threw up)/i,
    description: "Vomiting",
    recommendation: "Urgent assessment needed - evaluate hydration, medication timing, consider dose adjustment",
  },
  {
    pattern: /\bdiarrhea\b|loose (?:stool|stools|bowel)|bowel (?:issues|problems)/i,
    description: "Diarrhea",
    recommendation: "Assess severity and duration, counsel on hydration, consider probiotic or dose timing",
  },
  {
    pattern: /(?:constipation|constipated|can'?t go|haven'?t had a bowel movement)/i,
    description: "Constipation",
    recommendation: "Counsel on fiber intake and hydration, consider stool softener if medication-related",
  },
  {
    pattern: /(?:heartburn|acid reflux|gerd|indigestion)/i,
    description: "GERD/Heartburn",
    recommendation: "Review medications that may cause GI irritation, counsel on timing and diet modifications",
  },
  {
    pattern: /(?:loss of appetite|no appetite|don'?t feel like eating|not hungry)/i,
    description: "Appetite loss",
    recommendation: "Assess for medication-related cause, monitor weight, consider nutritional support",
  },
  {
    pattern: /(?:bloating|bloated|gas|flatulence)/i,
    description: "GI bloating/gas",
    recommendation: "Review medications and timing, counsel on dietary modifications",
  },

  // ============================================
  // NEUROLOGICAL
  // ============================================
  {
    pattern: /(?:dizziness|dizzy|lightheaded|light headed|vertigo)/i,
    description: "Dizziness/lightheadedness",
    recommendation: "Assess blood pressure, review medication timing, counsel on position changes",
  },
  {
    pattern: /(?:headache|headaches|migraine|head hurts)/i,
    description: "Headache",
    recommendation: "Evaluate frequency and severity, assess hydration, consider medication timing adjustment",
  },
  {
    pattern: /can'?t (?:sleep|fall asleep|stay asleep)|insomnia|trouble sleeping/i,
    description: "Sleep disturbance",
    recommendation: "Review medication timing, counsel on sleep hygiene, consider PM dosing adjustment",
  },
  {
    pattern: /can'?t (?:focus|concentrate|think clearly)|brain fog|foggy/i,
    description: "Cognitive effects",
    recommendation: "Assess for medication-related cognitive changes, consider clinical evaluation",
  },
  {
    pattern: /(?:numbness|tingling|pins and needles) (?:in |at )?(?:my |the )?(?:hands?|feet?|fingers?|toes?|arm|leg)/i,
    description: "Numbness/tingling (paresthesia)",
    recommendation: "Evaluate for neuropathy, check B12 levels if on metformin, consider neurological evaluation",
  },
  {
    pattern: /(?:memory (?:problems|issues|loss)|forgetting things|can'?t remember)/i,
    description: "Memory issues",
    recommendation: "Assess for medication-related cognitive effects, consider clinical evaluation",
  },
  {
    pattern: /(?:tremor|trembling|shaking|shaky hands)/i,
    description: "Tremor",
    recommendation: "Evaluate for medication-induced tremor, review beta-agonist or stimulant use",
  },
  {
    pattern: /(?:seizure|convulsion|fit)/i,
    description: "Seizure reported",
    recommendation: "URGENT: Immediate medical evaluation required, review all medications",
  },

  // ============================================
  // FATIGUE & ENERGY
  // ============================================
  {
    pattern: /(?:very |really |extremely |so )?(?:tired|exhausted|fatigued|no energy|weak)/i,
    description: "Fatigue/exhaustion",
    recommendation: "Evaluate for anemia or thyroid issues, assess medication timing and sleep patterns",
  },
  {
    pattern: /(?:feel|feeling) (?:run down|drained|worn out|wiped out)/i,
    description: "Energy depletion",
    recommendation: "Assess for underlying causes, review medication side effects, check labs if indicated",
  },
  {
    pattern: /(?:no motivation|lack of energy|can'?t get out of bed)/i,
    description: "Low energy/motivation",
    recommendation: "Screen for depression, assess thyroid function, review sedating medications",
  },

  // ============================================
  // SKIN REACTIONS
  // ============================================
  {
    pattern: /(?:rash|hives|itching|itchy|skin reaction)/i,
    description: "Skin reaction",
    recommendation: "Assess for allergic reaction, document affected areas, consider antihistamine or discontinuation",
  },
  {
    pattern: /(?:swelling|swollen) (?:face|lips|tongue|throat)/i,
    description: "Facial/throat swelling",
    recommendation: "URGENT: Assess for angioedema, may require immediate medical attention",
  },
  {
    pattern: /(?:dry skin|skin is dry|flaky skin|peeling skin)/i,
    description: "Dry/flaky skin",
    recommendation: "Counsel on moisturizers, assess hydration status, review retinoid or diuretic use",
  },
  {
    pattern: /(?:bruising easily|easy bruising|bruise easily)/i,
    description: "Easy bruising",
    recommendation: "Review anticoagulant therapy, check platelet count and INR if indicated",
  },
  {
    pattern: /(?:hair loss|losing hair|hair falling out|thinning hair)/i,
    description: "Hair loss",
    recommendation: "Evaluate for medication-related alopecia, check thyroid function",
  },

  // ============================================
  // SWELLING
  // ============================================
  {
    pattern: /(?:swelling|swollen) (?:in |on |at )?(?:my |the )?(?:arm|leg|hand|foot|ankle|wrist|limb)/i,
    description: "Limb swelling",
    recommendation: "Assess for edema or injection-related swelling, evaluate circulation and medication effects",
  },
  {
    pattern: /(?:significant|severe|bad|serious|a lot of) (?:swelling|swollen)/i,
    description: "Significant swelling reported",
    recommendation: "Urgent assessment needed - evaluate location, severity, and potential causes",
  },
  {
    pattern: /(?:my |the )?(?:arm|leg|hand|foot|ankle) (?:is |has )?(?:swelling|swollen|puffy)/i,
    description: "Limb swelling",
    recommendation: "Assess for edema, check for DVT risk if leg, evaluate medication side effects",
  },
  {
    pattern: /(?:edema|fluid retention|retaining water|holding water)/i,
    description: "Edema/fluid retention",
    recommendation: "Review calcium channel blocker or NSAID use, assess cardiac and renal function",
  },

  // ============================================
  // LYMPH NODE ISSUES
  // ============================================
  {
    pattern: /(?:swollen|enlarged|tender|painful) (?:lymph nodes?|glands?)/i,
    description: "Swollen lymph nodes",
    recommendation: "Assess for infection or immune response, may require clinical evaluation",
  },
  {
    pattern: /lymph nodes? (?:are |is |feel )?(?:swollen|enlarged|tender|painful|sore)/i,
    description: "Swollen lymph nodes",
    recommendation: "Assess for infection or immune response, may require clinical evaluation",
  },

  // ============================================
  // VISION ISSUES
  // ============================================
  {
    pattern: /(?:blurry vision|blurred vision|vision is blurry|can'?t see clearly)/i,
    description: "Blurred vision",
    recommendation: "Check blood glucose if diabetic, review medications affecting vision, consider ophthalmology referral",
  },
  {
    pattern: /(?:seeing (?:spots|flashes|floaters)|spots in.*vision|flashing lights)/i,
    description: "Visual disturbances",
    recommendation: "URGENT: Rule out retinal issues, especially if diabetic - ophthalmology referral needed",
  },
  {
    pattern: /(?:dry eyes|eyes are dry|burning eyes|eye irritation)/i,
    description: "Dry eyes",
    recommendation: "Review antihistamine or anticholinergic medications, recommend artificial tears",
  },
  {
    pattern: /(?:double vision|seeing double)/i,
    description: "Double vision",
    recommendation: "URGENT: Evaluate for neurological cause, medication review needed",
  },

  // ============================================
  // URINARY ISSUES
  // ============================================
  {
    pattern: /(?:frequent urination|urinating a lot|peeing a lot|going to bathroom a lot)/i,
    description: "Frequent urination",
    recommendation: "Check blood glucose, review diuretic use, assess for UTI symptoms",
  },
  {
    pattern: /(?:burning when I pee|painful urination|uti|urinary tract infection)/i,
    description: "Urinary symptoms",
    recommendation: "Assess for UTI, recommend hydration, may need urine culture",
  },
  {
    pattern: /(?:blood in.*urine|urine.*blood|hematuria)/i,
    description: "Blood in urine",
    recommendation: "URGENT: Medical evaluation needed, review anticoagulant therapy",
  },
  {
    pattern: /(?:incontinence|leaking urine|can'?t hold.*urine)/i,
    description: "Urinary incontinence",
    recommendation: "Review medications affecting bladder, consider urology referral",
  },

  // ============================================
  // BLOOD SUGAR ISSUES (Diabetes-related)
  // ============================================
  {
    pattern: /(?:blood sugar|glucose) (?:is |has been )?(?:high|low|dropping|spiking|all over the place)/i,
    description: "Blood sugar fluctuations",
    recommendation: "Review diabetes medication timing and dosing, assess diet and activity patterns",
  },
  {
    pattern: /(?:hypoglycemia|hypo|low blood sugar|sugar dropped)/i,
    description: "Hypoglycemia",
    recommendation: "Review insulin or sulfonylurea dosing, counsel on hypoglycemia management",
  },
  {
    pattern: /(?:feel shaky|feeling shaky|sweaty and shaky|jittery)/i,
    description: "Possible hypoglycemia symptoms",
    recommendation: "Check blood glucose, review diabetes medication timing, counsel on symptom recognition",
  },
  {
    pattern: /(?:sugar (?:is |running )?(?:too )?high|glucose (?:is |running )?high)/i,
    description: "Hyperglycemia",
    recommendation: "Review medication adherence, assess diet, consider therapy adjustment",
  },

  // ============================================
  // MOOD & MENTAL HEALTH
  // ============================================
  {
    pattern: /(?:feel|feeling) (?:depressed|down|hopeless|sad all the time)/i,
    description: "Depressive symptoms",
    recommendation: "Screen for depression, review medications that may affect mood, consider mental health referral",
  },
  {
    pattern: /(?:anxiety|anxious|nervous|worried all the time|panic)/i,
    description: "Anxiety symptoms",
    recommendation: "Assess for medication-related anxiety, review stimulant or corticosteroid use, consider support",
  },
  {
    pattern: /(?:mood swings|irritable|angry all the time|short temper)/i,
    description: "Mood changes",
    recommendation: "Review corticosteroid or hormonal medication use, assess for underlying causes",
  },
  {
    pattern: /(?:suicidal|want to die|thoughts of (?:hurting|harming) myself|self harm)/i,
    description: "CRITICAL: Suicidal ideation",
    recommendation: "URGENT: Immediate crisis intervention needed, connect with mental health resources",
  },

  // ============================================
  // WEIGHT CHANGES
  // ============================================
  {
    pattern: /(?:gained weight|weight gain|putting on weight|getting heavier)/i,
    description: "Weight gain",
    recommendation: "Review medications causing weight gain (insulin, steroids, antipsychotics), assess diet and activity",
  },
  {
    pattern: /(?:lost weight|losing weight|weight loss|getting thinner)/i,
    description: "Weight loss",
    recommendation: "Assess for unintentional weight loss, review GLP-1 agonist use, check thyroid function",
  },

  // ============================================
  // BLEEDING & BRUISING
  // ============================================
  {
    pattern: /(?:bleeding|bleed) (?:gums|nose|a lot)/i,
    description: "Abnormal bleeding",
    recommendation: "Review anticoagulant therapy, check INR/PT, assess for drug interactions",
  },
  {
    pattern: /(?:blood in.*stool|black stool|tarry stool|melena)/i,
    description: "GI bleeding signs",
    recommendation: "URGENT: Medical evaluation needed, review NSAID and anticoagulant use",
  },
  {
    pattern: /(?:coughing.*blood|blood.*cough|hemoptysis)/i,
    description: "Coughing blood",
    recommendation: "URGENT: Immediate medical evaluation required",
  },

  // ============================================
  // FEVER & INFECTION
  // ============================================
  {
    pattern: /(?:fever|temperature|running a fever|feel feverish)/i,
    description: "Fever",
    recommendation: "Assess for infection, review immunosuppressant use, may need medical evaluation",
  },
  {
    pattern: /(?:chills|shivering|shaking with cold)/i,
    description: "Chills",
    recommendation: "Assess for infection, check temperature, may indicate systemic illness",
  },
  {
    pattern: /(?:infection|infected|signs of infection)/i,
    description: "Infection concern",
    recommendation: "Evaluate infection signs, review immunosuppressant therapy, may need antibiotics",
  },

  // ============================================
  // ALLERGIC REACTIONS
  // ============================================
  {
    pattern: /(?:allergic|allergy|allergies) (?:to |reaction)/i,
    description: "Allergic reaction reported",
    recommendation: "Document allergy in profile, assess severity, consider alternative medication",
  },
  {
    pattern: /(?:anaphylaxis|throat closing|can'?t swallow|severe allergic)/i,
    description: "CRITICAL: Severe allergic reaction",
    recommendation: "URGENT: Immediate emergency care needed, epinephrine may be required",
  },

  // ============================================
  // EFFECTIVENESS ISSUES
  // ============================================
  {
    pattern: /(?:not working|doesn'?t work|isn'?t working|stopped working)/i,
    description: "Medication not effective",
    recommendation: "Assess adherence and proper administration, consider dose adjustment or therapy change",
  },
  {
    pattern: /(?:getting |condition is )?(?:worse|worsening|not better)/i,
    description: "Condition worsening",
    recommendation: "Clinical evaluation needed, assess adherence and potential therapy escalation",
  },
  {
    pattern: /(?:symptoms came back|relapse|flare up|flare-up)/i,
    description: "Symptom recurrence",
    recommendation: "Assess adherence, evaluate for disease progression, consider therapy adjustment",
  },

  // ============================================
  // ADHERENCE BARRIERS - COST & ACCESS
  // ============================================
  {
    pattern: /(?:can'?t afford|too expensive|cost too much|price is too high)/i,
    description: "Cost barrier reported",
    recommendation: "Explore patient assistance programs, generic alternatives, or manufacturer coupons",
  },
  {
    pattern: /(?:no insurance|insurance (?:won'?t|doesn'?t) cover|prior auth|denied)/i,
    description: "Insurance/coverage issue",
    recommendation: "Assist with prior authorization, explore alternatives, connect with financial assistance",
  },
  {
    pattern: /(?:can'?t get to pharmacy|no transportation|hard to pick up)/i,
    description: "Access barrier",
    recommendation: "Discuss mail-order options, delivery services, or 90-day supplies",
  },

  // ============================================
  // ADHERENCE BARRIERS - FORGETTING/CONFUSION
  // ============================================
  {
    pattern: /(?:forget|forgot|keep forgetting) (?:to take|my (?:medication|medicine|pills?))/i,
    description: "Forgetting to take medication",
    recommendation: "Counsel on pill organizers, alarms/reminders, simplify regimen if possible",
  },
  {
    pattern: /(?:confused about|don'?t understand|not sure how to take)/i,
    description: "Medication confusion",
    recommendation: "Provide clear medication counseling, written instructions, consider simplification",
  },
  {
    pattern: /(?:too many (?:pills|medications)|hard to keep track|complicated regimen)/i,
    description: "Complex regimen burden",
    recommendation: "Review for deprescribing opportunities, assess for combination products",
  },
  {
    pattern: /(?:ran out|run out|out of (?:my )?(?:medication|medicine|pills?))/i,
    description: "Medication supply gap",
    recommendation: "Assess for missed doses, review refill timing, set up auto-refill",
  },

  // ============================================
  // TAKING MEDICATIONS INCORRECTLY
  // ============================================
  {
    pattern: /(?:take|taking|took) (?:it |them )?(?:wrong|incorrectly|the wrong way)/i,
    description: "Incorrect medication use",
    recommendation: "Provide detailed administration counseling, assess for any adverse effects",
  },
  {
    pattern: /(?:doubled up|took (?:two|double|extra)|took too (?:much|many))/i,
    description: "Possible overdose/double dose",
    recommendation: "Assess for symptoms, provide guidance on what to do, monitor as needed",
  },
  {
    pattern: /(?:skipped|skipping|miss|missed) (?:a |some )?(?:dose|doses|days)/i,
    description: "Missed doses reported",
    recommendation: "Counsel on importance of adherence, discuss barriers, assess impact",
  },

  // ============================================
  // DRUG INTERACTIONS & CONCERNS
  // ============================================
  {
    pattern: /(?:interaction|interact) (?:with |between )/i,
    description: "Drug interaction concern",
    recommendation: "Review drug interaction, assess clinical significance, adjust therapy if needed",
  },
  {
    pattern: /(?:can I take.*with|take.*together|mix.*medications)/i,
    description: "Medication combination question",
    recommendation: "Review for interactions, provide guidance on safe timing and administration",
  },
  {
    pattern: /(?:started|taking|using) (?:a )?(?:new|another|different) (?:medication|medicine|supplement)/i,
    description: "New medication started",
    recommendation: "Review for interactions with existing therapy, update medication list",
  },

  // ============================================
  // GENERAL CONCERNS
  // ============================================
  {
    pattern: /side effect/i,
    description: "General side effect reported",
    recommendation: "Detailed assessment needed - identify specific symptoms and severity",
  },
  {
    pattern: /(?:bad |negative |adverse )?reaction/i,
    description: "Adverse reaction",
    recommendation: "Document reaction details, assess severity, determine if medication should be continued",
  },
  {
    pattern: /problem with (?:the |my |this )?(?:medication|medicine|drug)/i,
    description: "Medication problem reported",
    recommendation: "Clarify specific issue, assess if clinical intervention needed",
  },
  {
    pattern: /(?:don'?t like|hate|terrible|awful) (?:this |the |my )?(?:medication|medicine|drug)/i,
    description: "Patient dissatisfaction with medication",
    recommendation: "Explore specific concerns, assess for alternatives if clinically appropriate",
  },
  {
    pattern: /(?:want to stop|stop taking|quit taking|don'?t want to take)/i,
    description: "Desire to discontinue medication",
    recommendation: "Assess reasons, counsel on risks of stopping, explore alternatives if appropriate",
  },
];

// Patterns indicating profile/contact changes
const PROFILE_CHANGE_PATTERNS = [
  // Address changes
  /(?:I'?ve |I )?moved/i,
  /new address/i,
  /(?:staying|living) with (?:my )?(son|daughter|family|relative|kids|parents)/i,
  /different (?:phone|number|address|location)/i,
  /changed (?:my )?(?:phone|number|address|insurance)/i,
  /new (?:phone|number|address|insurance)/i,
  /(?:temporary|temporarily) at/i,
  /(?:visiting|staying at)/i,
  /update (?:my )?(?:address|phone|contact|information)/i,
  /no longer (?:at|live at|living at)/i,
  // Caregiver changes
  /(?:my |new )?(?:caregiver|aide|helper|nurse) (?:is|changed|left)/i,
  /(?:someone else|daughter|son|spouse) (?:is )?(?:now )?(?:picking up|managing|handling)/i,
  /(?:nursing home|assisted living|care facility|rehab|hospital)/i,
  // Insurance changes
  /(?:new|different|changed) (?:insurance|coverage|plan)/i,
  /(?:lost|losing|don'?t have) (?:my )?insurance/i,
  /(?:medicare|medicaid|tricare) (?:now|changed)/i,
  // Prescriber changes
  /(?:new|different|changed) (?:doctor|physician|prescriber|provider)/i,
  /(?:switched|changing|moved) (?:to a new )?(?:doctor|physician|clinic)/i,
  // Life changes
  /(?:hospitalized|was in the hospital|just got out of)/i,
  /(?:surgery|procedure|operation) (?:coming up|scheduled|had)/i,
  /(?:pregnant|expecting|going to have a baby)/i,
  /(?:nursing|breastfeeding)/i,
];

// Patterns indicating clinical concerns (safety, necessity questions)
const CLINICAL_CONCERN_PATTERNS = [
  // Safety questions
  /is (?:it|this) safe/i,
  /can I take (?:it|this) with/i,
  /(?:what|any) (?:interactions|interact)/i,
  /(?:should|do) I (?:still |continue to )?take/i,
  /(?:why|what) (?:is|do) (?:this|I need this) for/i,
  /(?:do|does) (?:this|it) (?:really )?work/i,
  /(?:how long|when) (?:should|do|will) I/i,
  /(?:worried|concerned|scared) about/i,
  /(?:is|are) there (?:any )?(?:alternatives|other options)/i,
  /(?:doctor|physician|prescriber) (?:said|told|mentioned)/i,
  // Administration questions
  /(?:how|when) (?:should|do) I take (?:this|it)/i,
  /(?:with food|without food|empty stomach)/i,
  /(?:morning|night|evening|bedtime) (?:or|vs)/i,
  /(?:what if I )?miss(?:ed)? a dose/i,
  /(?:can I )?(?:cut|split|crush|chew) (?:this|the|these)/i,
  // Storage questions
  /(?:how|where) (?:should|do) I (?:store|keep)/i,
  /(?:refrigerat|fridge|cold|room temperature)/i,
  /(?:expired|expiration|expire|out of date)/i,
  // Concerns about treatment
  /(?:am I going to be on this |do I have to take this )?(?:forever|for life|long term)/i,
  /(?:is this )?(?:necessary|really need|have to take)/i,
  /(?:what happens if I )?(?:stop|don'?t take|skip)/i,
  /(?:heard|read|seen) (?:bad things|negative|concerns) about/i,
  // Second opinion
  /(?:should I )?(?:get|seek) (?:a )?second opinion/i,
  /(?:want to )?talk to (?:my |the |a )?(?:doctor|pharmacist|specialist)/i,
];

// Common medication names for extraction
const MEDICATION_PATTERNS = [
  // Specialty medications
  /humira/i, /adalimumab/i,
  /enbrel/i, /etanercept/i,
  /stelara/i, /ustekinumab/i,
  /keytruda/i, /pembrolizumab/i,
  /opdivo/i, /nivolumab/i,
  /ozempic/i, /semaglutide/i,
  /trulicity/i, /dulaglutide/i,
  /dupixent/i, /dupilumab/i,
  // Common medications
  /metformin/i,
  /lisinopril/i,
  /atorvastatin/i,
  /amlodipine/i,
  /omeprazole/i,
  /levothyroxine/i,
  /gabapentin/i,
  /losartan/i,
  /hydrochlorothiazide/i,
  /metoprolol/i,
  /simvastatin/i,
  /pantoprazole/i,
  /prednisone/i,
  /furosemide/i,
  /insulin/i,
];

// ============================================
// DETECTION FUNCTIONS
// ============================================

/**
 * Analyze a transcript for adherence-related flags
 */
export function analyzeTranscript(messages: TranscriptMessage[]): DetectionResult {
  const flags: DetectionResult["flags"] = [];
  
  // Only analyze user (patient) messages
  const patientMessages = messages.filter(m => m.role === "user");
  const fullTranscript = patientMessages.map(m => m.message).join(" ");
  
  console.log("[AdherenceDetection] Analyzing transcript:", fullTranscript.substring(0, 200));
  
  // Check for declined medications
  const declineMatches = detectPatterns(fullTranscript, DECLINE_PATTERNS);
  console.log("[AdherenceDetection] Decline matches:", declineMatches);
  if (declineMatches.length > 0) {
    const medications = extractMedications(fullTranscript);
    flags.push({
      type: "adherence_gap",
      severity: medications.length > 1 ? "high" : "medium",
      details: {
        medicationsDeclined: medications.length > 0 ? medications.slice(1) : ["medication (unspecified)"],
        medicationsAccepted: medications.length > 0 ? [medications[0]] : [],
        transcriptExcerpt: getRelevantExcerpt(patientMessages, declineMatches[0]),
        detectedPatterns: declineMatches,
      },
      clinicalPillar: "adherence",
      interventionRecommendation: generateAdherenceIntervention(medications),
    });
  }
  
  // Check for adverse events with specific context
  const adverseEventResult = detectAdverseEventsWithContext(fullTranscript);
  console.log("[AdherenceDetection] Adverse event matches:", adverseEventResult.matches);
  console.log("[AdherenceDetection] Full transcript being analyzed:", fullTranscript);
  if (adverseEventResult.matches.length > 0) {
    // Use the most specific/severe match for the primary description
    const primaryEvent = adverseEventResult.details[0];
    flags.push({
      type: "adverse_event",
      severity: primaryEvent.description.includes("URGENT") || 
                primaryEvent.description.includes("swelling") ? "high" : "high",
      details: {
        eventDescription: primaryEvent.description,
        transcriptExcerpt: getRelevantExcerpt(patientMessages, adverseEventResult.matches[0]),
        detectedPatterns: adverseEventResult.matches,
      },
      clinicalPillar: "safety",
      interventionRecommendation: primaryEvent.recommendation,
    });
  }
  
  // Check for profile changes
  const profileMatches = detectPatterns(fullTranscript, PROFILE_CHANGE_PATTERNS);
  if (profileMatches.length > 0) {
    flags.push({
      type: "profile_change",
      severity: "low",
      details: {
        eventDescription: profileMatches.join(", "),
        transcriptExcerpt: getRelevantExcerpt(patientMessages, profileMatches[0]),
        detectedPatterns: profileMatches,
      },
      clinicalPillar: "unmet_needs",
      interventionRecommendation: "Update patient profile with new contact information. Verify shipping address if medication requires delivery.",
    });
  }
  
  // Check for clinical concerns
  const clinicalMatches = detectPatterns(fullTranscript, CLINICAL_CONCERN_PATTERNS);
  if (clinicalMatches.length > 0 && adverseEventResult.matches.length === 0) {
    flags.push({
      type: "clinical_concern",
      severity: "medium",
      details: {
        eventDescription: clinicalMatches.join(", "),
        transcriptExcerpt: getRelevantExcerpt(patientMessages, clinicalMatches[0]),
        detectedPatterns: clinicalMatches,
      },
      clinicalPillar: "effectiveness",
      interventionRecommendation: "Schedule pharmacist consultation to address patient questions about medication therapy.",
    });
  }
  
  return {
    hasFlags: flags.length > 0,
    flags,
  };
}

/**
 * Detect patterns in text and return matched phrases
 */
function detectPatterns(text: string, patterns: RegExp[]): string[] {
  const matches: string[] = [];
  
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      matches.push(match[0]);
    }
  }
  
  return matches;
}

/**
 * Detect adverse events with specific context (description and recommendation)
 */
function detectAdverseEventsWithContext(text: string): {
  matches: string[];
  details: { description: string; recommendation: string }[];
} {
  const matches: string[] = [];
  const details: { description: string; recommendation: string }[] = [];
  
  // Log text being checked (first 500 chars)
  console.log("[AdverseEventDetection] Checking text:", text.substring(0, 500));
  
  for (const item of ADVERSE_EVENT_PATTERNS_WITH_CONTEXT) {
    const match = text.match(item.pattern);
    if (match) {
      console.log("[AdverseEventDetection] MATCH found:", item.description, "->", match[0]);
      matches.push(match[0]);
      details.push({
        description: item.description,
        recommendation: item.recommendation,
      });
    }
  }
  
  if (matches.length === 0) {
    console.log("[AdverseEventDetection] No matches found in text");
  }
  
  return { matches, details };
}

/**
 * Extract medication names from transcript
 */
function extractMedications(text: string): string[] {
  const medications: string[] = [];
  
  for (const pattern of MEDICATION_PATTERNS) {
    const match = text.match(pattern);
    if (match) {
      // Capitalize first letter
      const medName = match[0].charAt(0).toUpperCase() + match[0].slice(1).toLowerCase();
      if (!medications.includes(medName)) {
        medications.push(medName);
      }
    }
  }
  
  return medications;
}

/**
 * Get relevant excerpt from transcript around the detected pattern
 */
function getRelevantExcerpt(messages: TranscriptMessage[], pattern: string): string {
  for (const msg of messages) {
    if (msg.message.toLowerCase().includes(pattern.toLowerCase())) {
      // Return the message, truncated if too long
      return msg.message.length > 200 
        ? msg.message.substring(0, 200) + "..."
        : msg.message;
    }
  }
  
  // Fallback: return the last patient message
  return messages[messages.length - 1]?.message || "";
}

/**
 * Generate intervention recommendation for adherence gaps
 */
function generateAdherenceIntervention(medications: string[]): string {
  if (medications.length === 0) {
    return "Follow up with patient to understand reason for declining refill. Assess for barriers to adherence (cost, side effects, complexity).";
  }
  
  if (medications.length === 1) {
    return `Patient declined refill. Schedule adherence counseling call to discuss importance of ${medications[0]} therapy and address any concerns.`;
  }
  
  return `Patient declined ${medications.length - 1} medication(s). Priority follow-up needed to assess adherence barriers and prevent therapy gaps. Consider MTM consultation.`;
}

// ============================================
// LOCAL STORAGE MANAGEMENT
// ============================================

const STORAGE_KEY = "asepha_adherence_flags";

export interface StoredAdherenceData {
  flags: AdherenceFlag[];
  lastUpdated: string;
  stats: {
    totalFlags: number;
    byType: Record<AdherenceFlag["type"], number>;
    bySeverity: Record<AdherenceFlag["severity"], number>;
  };
}

/**
 * Get all stored adherence flags
 */
export function getStoredFlags(): StoredAdherenceData {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error("Error reading adherence flags from localStorage:", error);
  }
  
  return {
    flags: [],
    lastUpdated: new Date().toISOString(),
    stats: {
      totalFlags: 0,
      byType: { adherence_gap: 0, adverse_event: 0, profile_change: 0, clinical_concern: 0 },
      bySeverity: { low: 0, medium: 0, high: 0 },
    },
  };
}

/**
 * Save a new adherence flag
 */
export function saveFlag(flag: AdherenceFlag): void {
  const data = getStoredFlags();
  
  // Check if flag for this call already exists to avoid duplicates
  const existingIndex = data.flags.findIndex(f => f.callId === flag.callId && f.type === flag.type);
  if (existingIndex >= 0) {
    // Update existing flag
    data.flags[existingIndex] = flag;
  } else {
    // Add new flag
    data.flags.unshift(flag);
  }
  
  // Update stats
  data.stats.totalFlags = data.flags.length;
  data.stats.byType = {
    adherence_gap: data.flags.filter(f => f.type === "adherence_gap").length,
    adverse_event: data.flags.filter(f => f.type === "adverse_event").length,
    profile_change: data.flags.filter(f => f.type === "profile_change").length,
    clinical_concern: data.flags.filter(f => f.type === "clinical_concern").length,
  };
  data.stats.bySeverity = {
    low: data.flags.filter(f => f.severity === "low").length,
    medium: data.flags.filter(f => f.severity === "medium").length,
    high: data.flags.filter(f => f.severity === "high").length,
  };
  data.lastUpdated = new Date().toISOString();
  
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

/**
 * Acknowledge a flag (mark as reviewed)
 */
export function acknowledgeFlag(flagId: string): void {
  const data = getStoredFlags();
  const flag = data.flags.find(f => f.id === flagId);
  if (flag) {
    flag.acknowledged = true;
    data.lastUpdated = new Date().toISOString();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }
}

/**
 * Update flag follow-up status
 */
export function updateFlagFollowUp(
  flagId: string, 
  status: AdherenceFlag["followUpStatus"], 
  callId?: string
): void {
  const data = getStoredFlags();
  const flag = data.flags.find(f => f.id === flagId);
  if (flag) {
    flag.followUpStatus = status;
    if (callId) {
      flag.followUpCallId = callId;
    }
    if (status === "in_progress" || status === "completed") {
      flag.followUpTimestamp = new Date().toISOString();
    }
    data.lastUpdated = new Date().toISOString();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }
}

/**
 * Update flag with sentiment analysis after follow-up call
 */
export function updateFlagSentiment(
  flagId: string, 
  sentiment: AdherenceFlag["followUpSentiment"]
): void {
  const data = getStoredFlags();
  const flag = data.flags.find(f => f.id === flagId);
  if (flag) {
    flag.followUpStatus = "completed";
    flag.followUpSentiment = sentiment;
    data.lastUpdated = new Date().toISOString();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }
}

/**
 * Generate demo sentiment based on adverse event type (hardcoded for demo)
 */
export function generateDemoSentiment(flag: AdherenceFlag): NonNullable<AdherenceFlag["followUpSentiment"]> {
  const eventDesc = flag.details.eventDescription?.toLowerCase() || "";
  
  // Hardcoded demo responses based on adverse event type
  if (eventDesc.includes("injection") || eventDesc.includes("site")) {
    return {
      overall: "neutral",
      notes: "Patient reported discomfort but understood technique improvements. Agreed to try rotating sites and applying cold compress.",
      keyMoments: [
        "Patient sounded relieved when discussing site rotation technique",
        "Slight hesitation when asked about injection compliance",
        "Expressed appreciation for the follow-up call"
      ]
    };
  }
  
  if (eventDesc.includes("muscle") || eventDesc.includes("weakness") || eventDesc.includes("limb")) {
    return {
      overall: "concerned",
      notes: "Patient expressed ongoing concern about muscle weakness affecting daily activities. Recommend clinical review and possible CK level check.",
      keyMoments: [
        "Patient sounded stressed when describing muscle weakness",
        "Mentioned avoiding stairs due to leg pain",
        "Asked multiple questions about alternative medications"
      ]
    };
  }
  
  if (eventDesc.includes("nausea") || eventDesc.includes("stomach") || eventDesc.includes("gi")) {
    return {
      overall: "neutral",
      notes: "Patient experiencing manageable GI symptoms. Agreed to take medication with food and monitor symptoms.",
      keyMoments: [
        "Patient seemed optimistic about dietary modifications",
        "Confirmed understanding of when to seek further help"
      ]
    };
  }
  
  if (eventDesc.includes("dizz") || eventDesc.includes("lightheaded")) {
    return {
      overall: "concerned",
      notes: "Patient reports dizziness affecting balance. Recommend blood pressure monitoring and medication timing review.",
      keyMoments: [
        "Patient expressed worry about falling",
        "Mentioned dizziness is worse in the morning",
        "Agreed to sit down when feeling lightheaded"
      ]
    };
  }
  
  if (eventDesc.includes("fatigue") || eventDesc.includes("tired") || eventDesc.includes("exhausted")) {
    return {
      overall: "neutral",
      notes: "Patient reports persistent fatigue. Discussed sleep habits and medication timing. Will monitor for improvement.",
      keyMoments: [
        "Patient noted fatigue started about 2 weeks after starting medication",
        "Agreed to try taking medication at bedtime"
      ]
    };
  }
  
  if (eventDesc.includes("rash") || eventDesc.includes("skin") || eventDesc.includes("itching")) {
    return {
      overall: "concerned",
      notes: "Patient reporting skin reaction. Documented location and appearance. Recommend clinical evaluation if spreading.",
      keyMoments: [
        "Patient sounded anxious about the rash",
        "Confirmed no breathing difficulties",
        "Will send photos to clinical team"
      ]
    };
  }
  
  // Default sentiment
  return {
    overall: "neutral",
    notes: "Patient follow-up completed. Symptoms discussed and documented. Patient understands when to seek additional help.",
    keyMoments: [
      "Patient confirmed understanding of medication instructions",
      "Expressed appreciation for proactive follow-up"
    ]
  };
}

/**
 * Clear all flags (for demo reset)
 */
export function clearAllFlags(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Remove flags with "Unknown Patient" from localStorage
 * (cleanup for stale data from before fix was applied)
 */
export function removeUnknownPatientFlags(): number {
  const data = localStorage.getItem(STORAGE_KEY);
  if (!data) return 0;
  
  try {
    const parsed: StoredAdherenceData = JSON.parse(data);
    const originalCount = parsed.flags.length;
    
    // Filter out "Unknown Patient" flags
    parsed.flags = parsed.flags.filter(
      (flag) => flag.patientName !== "Unknown Patient"
    );
    
    const removedCount = originalCount - parsed.flags.length;
    
    if (removedCount > 0) {
      // Recalculate stats
      parsed.stats = {
        totalFlags: parsed.flags.length,
        byType: {
          adherence_gap: parsed.flags.filter((f) => f.type === "adherence_gap").length,
          adverse_event: parsed.flags.filter((f) => f.type === "adverse_event").length,
          profile_change: parsed.flags.filter((f) => f.type === "profile_change").length,
          clinical_concern: parsed.flags.filter((f) => f.type === "clinical_concern").length,
        },
        bySeverity: {
          low: parsed.flags.filter((f) => f.severity === "low").length,
          medium: parsed.flags.filter((f) => f.severity === "medium").length,
          high: parsed.flags.filter((f) => f.severity === "high").length,
        },
      };
      parsed.lastUpdated = new Date().toISOString();
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
    }
    
    return removedCount;
  } catch {
    return 0;
  }
}

/**
 * Generate unique ID for a flag
 */
export function generateFlagId(): string {
  return `flag_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Seed demo data with a few sample adherence flags
 */
export function seedDemoFlags(): void {
  const now = new Date();
  
  const demoFlags: AdherenceFlag[] = [
    {
      id: generateFlagId(),
      callId: "demo-call-001",
      timestamp: new Date(now.getTime() - 45 * 60000).toISOString(), // 45 mins ago
      patientName: "Margaret Thompson",
      patientPhone: "+19056303961",
      type: "adherence_gap",
      severity: "high",
      details: {
        medicationsDeclined: ["Atorvastatin 40mg", "Lisinopril 10mg"],
        medicationsAccepted: ["Metformin 500mg"],
        transcriptExcerpt: "I only want to refill my diabetes medication. I don't need the other two right now.",
        detectedPatterns: ["only want to refill", "don't need the other"],
      },
      clinicalPillar: "adherence",
      interventionRecommendation: "Patient declined 2 medications. Priority follow-up needed to assess adherence barriers. High risk for cardiovascular event without statin and ACE inhibitor therapy.",
      acknowledged: false,
    },
    {
      id: generateFlagId(),
      callId: "demo-call-002", 
      timestamp: new Date(now.getTime() - 2 * 60 * 60000).toISOString(), // 2 hours ago
      patientName: "Robert Chen",
      patientPhone: "+19056303961",
      type: "adverse_event",
      severity: "high",
      details: {
        eventDescription: "Pain at injection site",
        transcriptExcerpt: "I've been having pain at the injection site for the past few days. It's sore and a little swollen where I give myself the shot.",
        detectedPatterns: ["pain at injection site", "sore"],
      },
      clinicalPillar: "safety",
      interventionRecommendation: "Pharmacist counsel on rotating injection sites and proper injection technique",
      acknowledged: false,
      followUpStatus: "pending",
    },
    {
      id: generateFlagId(),
      callId: "demo-call-003",
      timestamp: new Date(now.getTime() - 4 * 60 * 60000).toISOString(), // 4 hours ago
      patientName: "Dorothy Williams",
      patientPhone: "+19056303961",
      type: "profile_change",
      severity: "low",
      details: {
        eventDescription: "Patient relocated to daughter's residence",
        transcriptExcerpt: "I've moved in with my daughter now. Can you update my address? I'm staying at her place in Phoenix.",
        detectedPatterns: ["moved", "staying with my daughter", "update my address"],
      },
      clinicalPillar: "unmet_needs",
      interventionRecommendation: "Update patient profile with new contact information. Verify shipping address for medication delivery. Consider care coordination with new local providers.",
      acknowledged: true, // Show one acknowledged
    },
  ];
  
  // Save the demo flags
  const data: StoredAdherenceData = {
    flags: demoFlags,
    lastUpdated: new Date().toISOString(),
    stats: {
      totalFlags: demoFlags.length,
      byType: {
        adherence_gap: demoFlags.filter(f => f.type === "adherence_gap").length,
        adverse_event: demoFlags.filter(f => f.type === "adverse_event").length,
        profile_change: demoFlags.filter(f => f.type === "profile_change").length,
        clinical_concern: demoFlags.filter(f => f.type === "clinical_concern").length,
      },
      bySeverity: {
        low: demoFlags.filter(f => f.severity === "low").length,
        medium: demoFlags.filter(f => f.severity === "medium").length,
        high: demoFlags.filter(f => f.severity === "high").length,
      },
    },
  };
  
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}
